import { useCallback, useEffect, useMemo, useState } from 'react';

import isEmpty from 'lodash-es/isEmpty';
import { FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { VariantSelector } from '@/components/ui/custom/VariantSelector';
import { Option } from '@/shared/types/rules';
import { LENGTH_XXL } from '@/util/validation/constants';

import { CharacterCounter } from './CharacterCounter';
import { EditorWrapper } from './EditorWrapper';

interface ToolkitEditorTabsProps {
  values: string[]; // Array of markdown content
  onChange: (index: number, content: string) => void;
  variableOptions: Option[];
  errors?: FieldErrors<{ textValue: string }>[];
  placeholder?: string;
  onDeleteVariant?: (index: number) => void;
  onAddVariant?: () => void;
}

/**
 * Select dropdown wrapper for ToolkitEditor instances
 * Supports multiple editor variants with dropdown navigation
 */
export function ToolkitEditorTabs({
  values,
  onChange,
  variableOptions,
  errors = [],
  placeholder,
  onDeleteVariant,
  onAddVariant,
}: ToolkitEditorTabsProps) {
  const { t } = useTranslation();
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Auto-focus tab with error
  useEffect(() => {
    if (errors.length > 0) {
      // Find first tab with error
      const errorTabIndex = errors.findIndex((error) => !isEmpty(error));
      if (errorTabIndex !== -1) {
        setActiveTabIndex(errorTabIndex);
      }
    }
  }, [errors]);

  // Stable callback for content changes - memoized per active index
  const handleContentChange = useCallback(
    (content: string) => {
      onChange(activeTabIndex, content);
    },
    [onChange, activeTabIndex]
  );

  const errorMessage = errors[activeTabIndex]?.textValue?.message;
  const hasError = !!errorMessage;

  const handleTabChange = useCallback((index: number) => {
    setActiveTabIndex(index);
  }, []);

  const handleDeleteVariant = useCallback(
    (index: number) => {
      if (onDeleteVariant && index > 0) {
        // Switch to previous variant if deleting active one
        if (index === activeTabIndex) {
          setActiveTabIndex(index - 1);
        } else if (index < activeTabIndex) {
          // Adjust active index if deleting earlier variant
          setActiveTabIndex(activeTabIndex - 1);
        }
        onDeleteVariant(index);
      }
    },
    [activeTabIndex, onDeleteVariant]
  );

  const handleAddVariant = useCallback(() => {
    if (onAddVariant) {
      onAddVariant();
      setActiveTabIndex(values.length); // Select the newly added variant
    }
  }, [onAddVariant, values.length]);

  const getVariantLabel = useCallback(
    (index: number) => {
      return index === 0
        ? t('rich_text_editor.default_reply')
        : `${t('rich_text_editor.variant')} ${index + 1}`;
    },
    [t]
  );

  const variantItems = useMemo(
    () => values.map((_, index) => ({ index, label: getVariantLabel(index) })),
    [values, getVariantLabel]
  );

  const currentContent = values[activeTabIndex];
  const currentLength = currentContent?.length || 0;

  return (
    <>
      <div className="mb-4">
        <VariantSelector
          items={variantItems}
          activeIndex={activeTabIndex}
          onSelect={handleTabChange}
          onDelete={onDeleteVariant ? handleDeleteVariant : undefined}
          onAdd={onAddVariant ? handleAddVariant : undefined}
          addButtonText={t('rich_text_editor.add_variant')}
          hasError={hasError}
        />
      </div>

      <CharacterCounter
        currentLength={currentLength}
        maxLength={LENGTH_XXL}
        label={t('common.reply')}
      />

      <EditorWrapper
        content={currentContent}
        onContentChange={handleContentChange}
        variableOptions={variableOptions}
        placeholder={placeholder}
      />

      {errorMessage && <p className="mt-1 text-sm text-destructive">{errorMessage}</p>}
    </>
  );
}
